'use client';
import React, { useState, useRef, useCallback, useEffect } from 'react';
import AppLayout from '@/components/AppLayout';
import Link from 'next/link';
import { ArrowLeft, Map, List, Search, Send, CheckCircle, AlertTriangle, X, Edit3, CheckSquare, Square, Layers, Trash2, Save, Info, Pencil, MousePointer, RotateCcw, Building2, Home, Warehouse, TreePine, Globe, Download,  } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


// ─── Types ────────────────────────────────────────────────────────────────────

type ZoneClass = 'Zone A' | 'Zone B' | 'Zone C' | 'Zone D' | 'Zone E';
type PropertyType = 'Residential' | 'Commercial' | 'Industrial' | 'Vacant Land' | 'Farmland' | 'Mixed Use';
type ParcelStatus = 'draft' | 'delineated' | 'classified' | 'verified' | 'forwarded';

interface Point { x: number; y: number; }

interface Parcel {
  id: string;
  parcelId: string;
  sourcePropertyId: string;
  ownerName: string;
  county: string;
  district: string;
  propertyType: PropertyType;
  zoneClass: ZoneClass;
  areaSqm: number;
  boundaryPoints: Point[];
  status: ParcelStatus;
  delineatedBy: string;
  delineatedDate: string;
  notes: string;
  selected: boolean;
}

type DrawMode = 'select' | 'draw' | 'edit';
type ActiveView = 'map' | 'list';

// ─── Constants ────────────────────────────────────────────────────────────────

const ZONE_COLORS: Record<ZoneClass, string> = {
  'Zone A': '#f59e0b',
  'Zone B': '#60a5fa',
  'Zone C': '#34d399',
  'Zone D': '#a78bfa',
  'Zone E': '#fb7185',
};

const ZONE_DESCRIPTIONS: Record<ZoneClass, string> = {
  'Zone A': 'Central Business District — highest density, premium valuation',
  'Zone B': 'Urban Residential — medium-high density, established infrastructure',
  'Zone C': 'Peri-Urban — mixed use, developing infrastructure',
  'Zone D': 'Rural Residential — low density, limited services',
  'Zone E': 'Agricultural / Remote — farmland, forest, undeveloped',
};

const PROPERTY_ICONS: Record<PropertyType, React.ElementType> = {
  'Residential': Home,
  'Commercial': Building2,
  'Industrial': Warehouse,
  'Vacant Land': TreePine,
  'Farmland': Globe,
  'Mixed Use': Layers,
};

const PROPERTY_COLORS: Record<PropertyType, string> = {
  'Residential': '#60a5fa',
  'Commercial': '#f59e0b',
  'Industrial': '#f97316',
  'Vacant Land': '#34d399',
  'Farmland': '#a3e635',
  'Mixed Use': '#fb7185',
};

const STATUS_CONFIG: Record<ParcelStatus, { label: string; color: string; bg: string }> = {
  draft:       { label: 'Draft',       color: '#94a3b8', bg: 'bg-slate-700/60' },
  delineated:  { label: 'Delineated',  color: '#60a5fa', bg: 'bg-blue-900/40' },
  classified:  { label: 'Classified',  color: '#a78bfa', bg: 'bg-purple-900/40' },
  verified:    { label: 'Verified',    color: '#34d399', bg: 'bg-emerald-900/40' },
  forwarded:   { label: 'Fwd → S3',   color: '#f59e0b', bg: 'bg-amber-900/40' },
};

const COUNTIES = [
  'Montserrado','Margibi','Bong','Nimba','Lofa','Grand Bassa',
  'Sinoe','Maryland','Grand Cape Mount','Grand Gedeh','Rivercess',
  'Gbarpolu','River Gee','Grand Kru','Bomi',
];

// ─── Mock incoming Stage 1 properties ────────────────────────────────────────

const STAGE1_QUEUE = [
  { id: 'S1-0041', owner: 'James Kollie', county: 'Montserrado', district: 'Congo Town', type: 'Residential' as PropertyType, area: 420 },
  { id: 'S1-0042', owner: 'Fatu Kamara', county: 'Montserrado', district: 'Sinkor', type: 'Commercial' as PropertyType, area: 870 },
  { id: 'S1-0043', owner: 'Emmanuel Doe', county: 'Margibi', district: 'Kakata', type: 'Farmland' as PropertyType, area: 5200 },
  { id: 'S1-0044', owner: 'Patience Weah', county: 'Bong', district: 'Gbarnga', type: 'Residential' as PropertyType, area: 310 },
  { id: 'S1-0045', owner: 'Moses Flomo', county: 'Nimba', district: 'Sanniquellie', type: 'Industrial' as PropertyType, area: 1800 },
];

// ─── Initial parcels ──────────────────────────────────────────────────────────

const INITIAL_PARCELS: Parcel[] = [
  {
    id: 'p1', parcelId: 'LRA-MNT-2024-0001', sourcePropertyId: 'S1-0031',
    ownerName: 'Albert Sirleaf', county: 'Montserrado', district: 'Paynesville',
    propertyType: 'Residential', zoneClass: 'Zone B', areaSqm: 380,
    boundaryPoints: [{ x: 120, y: 80 }, { x: 200, y: 80 }, { x: 200, y: 150 }, { x: 120, y: 150 }],
    status: 'verified', delineatedBy: 'Staff: D. Kollie', delineatedDate: '2024-11-12', notes: '', selected: false,
  },
  {
    id: 'p2', parcelId: 'LRA-MNT-2024-0002', sourcePropertyId: 'S1-0032',
    ownerName: 'Grace Tamba', county: 'Montserrado', district: 'Congo Town',
    propertyType: 'Commercial', zoneClass: 'Zone A', areaSqm: 760,
    boundaryPoints: [{ x: 230, y: 60 }, { x: 340, y: 60 }, { x: 340, y: 160 }, { x: 230, y: 160 }],
    status: 'classified', delineatedBy: 'Staff: M. Pewee', delineatedDate: '2024-11-14', notes: '', selected: false,
  },
  {
    id: 'p3', parcelId: 'LRA-MNT-2024-0003', sourcePropertyId: 'S1-0033',
    ownerName: 'David Nimely', county: 'Montserrado', district: 'Sinkor',
    propertyType: 'Mixed Use', zoneClass: 'Zone A', areaSqm: 1100,
    boundaryPoints: [{ x: 360, y: 70 }, { x: 480, y: 70 }, { x: 480, y: 180 }, { x: 360, y: 180 }],
    status: 'delineated', delineatedBy: 'Staff: D. Kollie', delineatedDate: '2024-11-15', notes: '', selected: false,
  },
  {
    id: 'p4', parcelId: 'LRA-MRG-2024-0001', sourcePropertyId: 'S1-0034',
    ownerName: 'Hawa Mulbah', county: 'Margibi', district: 'Kakata',
    propertyType: 'Farmland', zoneClass: 'Zone E', areaSqm: 6400,
    boundaryPoints: [{ x: 100, y: 220 }, { x: 260, y: 220 }, { x: 260, y: 340 }, { x: 100, y: 340 }],
    status: 'verified', delineatedBy: 'Staff: F. Kpoto', delineatedDate: '2024-11-10', notes: '', selected: false,
  },
  {
    id: 'p5', parcelId: 'LRA-BNG-2024-0001', sourcePropertyId: 'S1-0035',
    ownerName: 'Thomas Konneh', county: 'Bong', district: 'Gbarnga',
    propertyType: 'Industrial', zoneClass: 'Zone C', areaSqm: 2200,
    boundaryPoints: [{ x: 300, y: 230 }, { x: 460, y: 230 }, { x: 460, y: 330 }, { x: 300, y: 330 }],
    status: 'draft', delineatedBy: 'Staff: M. Pewee', delineatedDate: '2024-11-16', notes: '', selected: false,
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function generateParcelId(county: string, year = 2024, seq: number) {
  const code = county.slice(0, 3).toUpperCase();
  return `LRA-${code}-${year}-${String(seq).padStart(4, '0')}`;
}

function polygonArea(pts: Point[]) {
  let area = 0;
  for (let i = 0; i < pts.length; i++) {
    const j = (i + 1) % pts.length;
    area += pts[i].x * pts[j].y;
    area -= pts[j].x * pts[i].y;
  }
  return Math.abs(area / 2);
}

function centroid(pts: Point[]): Point {
  const cx = pts.reduce((s, p) => s + p.x, 0) / pts.length;
  const cy = pts.reduce((s, p) => s + p.y, 0) / pts.length;
  return { x: cx, y: cy };
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function ZoneLegend() {
  return (
    <div className="flex flex-wrap gap-2">
      {(Object.entries(ZONE_COLORS) as [ZoneClass, string][]).map(([zone, color]) => (
        <div key={zone} className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm flex-shrink-0" style={{ background: color, opacity: 0.8 }} />
          <span className="text-xs text-slate-400">{zone}</span>
        </div>
      ))}
    </div>
  );
}

interface ParcelDetailPanelProps {
  parcel: Parcel;
  onClose: () => void;
  onSave: (updated: Parcel) => void;
}

function ParcelDetailPanel({ parcel, onClose, onSave }: ParcelDetailPanelProps) {
  const [form, setForm] = useState({ ...parcel });

  const set = (key: keyof Parcel, val: string | number) =>
    setForm(f => ({ ...f, [key]: val }));

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700/60">
        <div>
          <p className="text-xs text-slate-500 font-mono">{form.parcelId}</p>
          <h3 className="text-sm font-semibold text-white">{form.ownerName}</h3>
        </div>
        <button onClick={onClose} className="p-1 rounded hover:bg-slate-700 text-slate-400 hover:text-white transition-colors">
          <X size={16} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
        {/* Parcel ID */}
        <div>
          <label className="block text-xs text-slate-400 mb-1">Parcel ID</label>
          <input
            value={form.parcelId}
            onChange={e => set('parcelId', e.target.value)}
            className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white font-mono focus:outline-none focus:border-amber-500"
          />
        </div>

        {/* Zone Classification */}
        <div>
          <label className="block text-xs text-slate-400 mb-1">Zone Classification</label>
          <select
            value={form.zoneClass}
            onChange={e => set('zoneClass', e.target.value as ZoneClass)}
            className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-amber-500"
          >
            {(Object.keys(ZONE_COLORS) as ZoneClass[]).map(z => (
              <option key={z} value={z}>{z}</option>
            ))}
          </select>
          <p className="text-xs text-slate-500 mt-1">{ZONE_DESCRIPTIONS[form.zoneClass]}</p>
        </div>

        {/* Property Type */}
        <div>
          <label className="block text-xs text-slate-400 mb-1">Property Type</label>
          <select
            value={form.propertyType}
            onChange={e => set('propertyType', e.target.value as PropertyType)}
            className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-amber-500"
          >
            {(Object.keys(PROPERTY_ICONS) as PropertyType[]).map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>

        {/* County / District */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-slate-400 mb-1">County</label>
            <select
              value={form.county}
              onChange={e => set('county', e.target.value)}
              className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-amber-500"
            >
              {COUNTIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">District</label>
            <input
              value={form.district}
              onChange={e => set('district', e.target.value)}
              className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-amber-500"
            />
          </div>
        </div>

        {/* Area */}
        <div>
          <label className="block text-xs text-slate-400 mb-1">Area (m²)</label>
          <input
            type="number"
            value={form.areaSqm}
            onChange={e => set('areaSqm', Number(e.target.value))}
            className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-amber-500"
          />
        </div>

        {/* Status */}
        <div>
          <label className="block text-xs text-slate-400 mb-1">Status</label>
          <select
            value={form.status}
            onChange={e => set('status', e.target.value as ParcelStatus)}
            className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-amber-500"
          >
            {(Object.keys(STATUS_CONFIG) as ParcelStatus[]).map(s => (
              <option key={s} value={s}>{STATUS_CONFIG[s].label}</option>
            ))}
          </select>
        </div>

        {/* Notes */}
        <div>
          <label className="block text-xs text-slate-400 mb-1">Notes</label>
          <textarea
            value={form.notes}
            onChange={e => set('notes', e.target.value)}
            rows={3}
            className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-amber-500 resize-none"
            placeholder="Boundary notes, field observations..."
          />
        </div>

        {/* Boundary Points */}
        <div>
          <label className="block text-xs text-slate-400 mb-1">Boundary Vertices ({form.boundaryPoints.length} pts)</label>
          <div className="bg-slate-800/60 rounded-lg p-2 max-h-28 overflow-y-auto">
            {form.boundaryPoints.map((pt, i) => (
              <div key={i} className="flex items-center gap-2 text-xs text-slate-400 py-0.5">
                <span className="text-slate-600 w-4">{i + 1}</span>
                <span className="font-mono">x: {pt.x.toFixed(1)}</span>
                <span className="font-mono">y: {pt.y.toFixed(1)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="px-4 py-3 border-t border-slate-700/60 flex gap-2">
        <button
          onClick={() => onSave(form)}
          className="flex-1 flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold text-sm py-2 rounded-lg transition-colors"
        >
          <Save size={14} />
          Save Changes
        </button>
        <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-600 text-slate-400 hover:text-white hover:border-slate-500 text-sm transition-colors">
          Cancel
        </button>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function ParcelDelineationClient() {
  const [parcels, setParcels] = useState<Parcel[]>(INITIAL_PARCELS);
  const [activeView, setActiveView] = useState<ActiveView>('map');
  const [drawMode, setDrawMode] = useState<DrawMode>('select');
  const [drawPoints, setDrawPoints] = useState<Point[]>([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [selectedParcel, setSelectedParcel] = useState<Parcel | null>(null);
  const [hoveredParcel, setHoveredParcel] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<ParcelStatus | 'all'>('all');
  const [filterZone, setFilterZone] = useState<ZoneClass | 'all'>('all');
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showNewParcelForm, setShowNewParcelForm] = useState(false);
  const [newParcelForm, setNewParcelForm] = useState({
    sourcePropertyId: '',
    ownerName: '',
    county: 'Montserrado',
    district: '',
    propertyType: 'Residential' as PropertyType,
    zoneClass: 'Zone B' as ZoneClass,
    areaSqm: 0,
    notes: '',
  });
  const [batchForwardResult, setBatchForwardResult] = useState<{ count: number } | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const SVG_W = 600;
  const SVG_H = 420;

  // ── Derived ──────────────────────────────────────────────────────────────────

  const filteredParcels = parcels.filter(p => {
    const q = searchQuery.toLowerCase();
    const matchSearch = !q || p.parcelId.toLowerCase().includes(q) || p.ownerName.toLowerCase().includes(q) || p.county.toLowerCase().includes(q);
    const matchStatus = filterStatus === 'all' || p.status === filterStatus;
    const matchZone = filterZone === 'all' || p.zoneClass === filterZone;
    return matchSearch && matchStatus && matchZone;
  });

  const selectedCount = parcels.filter(p => p.selected).length;
  const verifiedCount = parcels.filter(p => p.status === 'verified' || p.status === 'classified').length;
  const forwardedCount = parcels.filter(p => p.status === 'forwarded').length;

  const kpis = [
    { label: 'Total Parcels', value: parcels.length, color: 'text-blue-400', icon: Layers },
    { label: 'Delineated', value: parcels.filter(p => p.status !== 'draft').length, color: 'text-purple-400', icon: Map },
    { label: 'Ready for S3', value: verifiedCount, color: 'text-emerald-400', icon: CheckCircle },
    { label: 'Forwarded → S3', value: forwardedCount, color: 'text-amber-400', icon: Send },
  ];

  // ── SVG Map Interaction ───────────────────────────────────────────────────────

  const getSVGPoint = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const rect = svg.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width) * SVG_W,
      y: ((e.clientY - rect.top) / rect.height) * SVG_H,
    };
  }, []);

  const handleSVGClick = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    if (drawMode !== 'draw') return;
    const pt = getSVGPoint(e);
    setDrawPoints(prev => [...prev, pt]);
    setIsDrawing(true);
  }, [drawMode, getSVGPoint]);

  const handleSVGDblClick = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    if (drawMode !== 'draw' || drawPoints.length < 3) return;
    e.preventDefault();
    setShowNewParcelForm(true);
    setIsDrawing(false);
  }, [drawMode, drawPoints]);

  const cancelDraw = () => {
    setDrawPoints([]);
    setIsDrawing(false);
    setDrawMode('select');
  };

  const confirmNewParcel = () => {
    if (drawPoints.length < 3) return;
    const seq = parcels.length + 1;
    const pid = generateParcelId(newParcelForm.county, 2024, seq);
    let area = Math.round(polygonArea(drawPoints) * 0.5);
    const newParcel: Parcel = {
      id: `p${Date.now()}`,
      parcelId: pid,
      sourcePropertyId: newParcelForm.sourcePropertyId || 'S1-MANUAL',
      ownerName: newParcelForm.ownerName || 'Unknown Owner',
      county: newParcelForm.county,
      district: newParcelForm.district,
      propertyType: newParcelForm.propertyType,
      zoneClass: newParcelForm.zoneClass,
      areaSqm: newParcelForm.areaSqm || area,
      boundaryPoints: drawPoints,
      status: 'delineated',
      delineatedBy: 'Staff: Current User',
      delineatedDate: new Date().toISOString().split('T')[0],
      notes: newParcelForm.notes,
      selected: false,
    };
    setParcels(prev => [...prev, newParcel]);
    setDrawPoints([]);
    setIsDrawing(false);
    setDrawMode('select');
    setShowNewParcelForm(false);
    setNewParcelForm({ sourcePropertyId: '', ownerName: '', county: 'Montserrado', district: '', propertyType: 'Residential', zoneClass: 'Zone B', areaSqm: 0, notes: '' });
  };

  const handleParcelClick = (parcel: Parcel, e: React.MouseEvent) => {
    e.stopPropagation();
    if (drawMode === 'select') {
      setSelectedParcel(parcel);
    }
  };

  const toggleSelect = (id: string) => {
    setParcels(prev => prev.map(p => p.id === id ? { ...p, selected: !p.selected } : p));
  };

  const toggleSelectAll = () => {
    const allSelected = filteredParcels.every(p => p.selected);
    const ids = new Set(filteredParcels.map(p => p.id));
    setParcels(prev => prev.map(p => ids.has(p.id) ? { ...p, selected: !allSelected } : p));
  };

  const saveParcel = (updated: Parcel) => {
    setParcels(prev => prev.map(p => p.id === updated.id ? updated : p));
    setSelectedParcel(updated);
  };

  const deleteParcel = (id: string) => {
    setParcels(prev => prev.filter(p => p.id !== id));
    if (selectedParcel?.id === id) setSelectedParcel(null);
  };

  const batchForwardToStage3 = () => {
    const eligible = parcels.filter(p => p.selected && (p.status === 'verified' || p.status === 'classified'));
    if (eligible.length === 0) return;
    const ids = new Set(eligible.map(p => p.id));
    setParcels(prev => prev.map(p => ids.has(p.id) ? { ...p, status: 'forwarded', selected: false } : p));
    setBatchForwardResult({ count: eligible.length });
    setShowBatchModal(false);
  };

  const importFromStage1 = (item: typeof STAGE1_QUEUE[0]) => {
    const seq = parcels.length + 1;
    const pid = generateParcelId(item.county, 2024, seq);
    const newParcel: Parcel = {
      id: `p${Date.now()}`,
      parcelId: pid,
      sourcePropertyId: item.id,
      ownerName: item.owner,
      county: item.county,
      district: '',
      propertyType: item.type,
      zoneClass: 'Zone C',
      areaSqm: item.area,
      boundaryPoints: [
        { x: 50 + Math.random() * 400, y: 50 + Math.random() * 300 },
        { x: 50 + Math.random() * 400, y: 50 + Math.random() * 300 },
        { x: 50 + Math.random() * 400, y: 50 + Math.random() * 300 },
        { x: 50 + Math.random() * 400, y: 50 + Math.random() * 300 },
      ],
      status: 'draft',
      delineatedBy: 'Staff: Current User',
      delineatedDate: new Date().toISOString().split('T')[0],
      notes: `Imported from Stage 1 — ${item.id}`,
      selected: false,
    };
    setParcels(prev => [...prev, newParcel]);
  };

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <AppLayout>
      <div className="min-h-screen bg-slate-950 text-white">
        {/* Header */}
        <div className="bg-slate-900 border-b border-slate-700/60 px-6 py-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Link href="/property-tax-cycle" className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition-colors">
                <ArrowLeft size={18} />
              </Link>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold bg-blue-600 text-white px-2 py-0.5 rounded-full">S2</span>
                  <h1 className="text-lg font-bold text-white">Parcel Delineation & Digitization</h1>
                </div>
                <p className="text-xs text-slate-400 mt-0.5">Draw boundaries · Assign parcel IDs · Set zone classification · Forward to Stage 3</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowImportModal(true)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-600 text-slate-300 hover:text-white hover:border-slate-500 text-sm transition-colors"
              >
                <Download size={14} />
                Import from S1
              </button>
              <button
                onClick={() => setShowBatchModal(true)}
                disabled={selectedCount === 0}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 disabled:opacity-40 disabled:cursor-not-allowed text-slate-900 font-semibold text-sm transition-colors"
              >
                <Send size={14} />
                Forward to S3 {selectedCount > 0 && `(${selectedCount})`}
              </button>
            </div>
          </div>
        </div>

        {/* KPI Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-700/40 border-b border-slate-700/60">
          {kpis.map(kpi => {
            const Icon = kpi.icon;
            return (
              <div key={kpi.label} className="bg-slate-900 px-5 py-3 flex items-center gap-3">
                <Icon size={18} className={kpi.color} />
                <div>
                  <p className={`text-xl font-bold ${kpi.color}`}>{kpi.value}</p>
                  <p className="text-xs text-slate-500">{kpi.label}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Batch forward success banner */}
        {batchForwardResult && (
          <div className="mx-6 mt-4 flex items-center gap-3 bg-emerald-900/40 border border-emerald-700/60 rounded-xl px-4 py-3">
            <CheckCircle size={16} className="text-emerald-400 flex-shrink-0" />
            <p className="text-sm text-emerald-300">
              <strong>{batchForwardResult.count} parcel{batchForwardResult.count > 1 ? 's' : ''}</strong> successfully forwarded to Stage 3 Verification.
            </p>
            <button onClick={() => setBatchForwardResult(null)} className="ml-auto text-emerald-500 hover:text-emerald-300">
              <X size={14} />
            </button>
          </div>
        )}

        {/* View Toggle + Filters */}
        <div className="px-6 py-3 flex items-center gap-3 flex-wrap border-b border-slate-800">
          <div className="flex bg-slate-800 rounded-lg p-0.5">
            {(['map', 'list'] as ActiveView[]).map(v => (
              <button
                key={v}
                onClick={() => setActiveView(v)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors capitalize ${activeView === v ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-white'}`}
              >
                {v === 'map' ? <Map size={14} /> : <List size={14} />}
                {v === 'map' ? 'Map View' : 'List View'}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 bg-slate-800 rounded-lg px-3 py-2 flex-1 max-w-xs">
            <Search size={14} className="text-slate-500" />
            <input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search parcel ID, owner, county..."
              className="bg-transparent text-sm text-white placeholder-slate-500 focus:outline-none w-full"
            />
          </div>

          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value as ParcelStatus | 'all')}
            className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-300 focus:outline-none focus:border-amber-500"
          >
            <option value="all">All Statuses</option>
            {(Object.keys(STATUS_CONFIG) as ParcelStatus[]).map(s => (
              <option key={s} value={s}>{STATUS_CONFIG[s].label}</option>
            ))}
          </select>

          <select
            value={filterZone}
            onChange={e => setFilterZone(e.target.value as ZoneClass | 'all')}
            className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-300 focus:outline-none focus:border-amber-500"
          >
            <option value="all">All Zones</option>
            {(Object.keys(ZONE_COLORS) as ZoneClass[]).map(z => (
              <option key={z} value={z}>{z}</option>
            ))}
          </select>
        </div>

        {/* Main Content */}
        <div className="flex h-[calc(100vh-280px)] min-h-[500px]">
          {/* Map View */}
          {activeView === 'map' && (
            <>
              {/* Map Canvas */}
              <div className="flex-1 flex flex-col bg-slate-950 relative">
                {/* Toolbar */}
                <div className="flex items-center gap-2 px-4 py-2 bg-slate-900/80 border-b border-slate-800">
                  <span className="text-xs text-slate-500 mr-1">Tools:</span>
                  {([
                    { mode: 'select' as DrawMode, icon: MousePointer, label: 'Select' },
                    { mode: 'draw' as DrawMode, icon: Pencil, label: 'Draw Boundary' },
                  ]).map(({ mode, icon: Icon, label }) => (
                    <button
                      key={mode}
                      onClick={() => { setDrawMode(mode); if (mode !== 'draw') cancelDraw(); }}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${drawMode === mode ? 'bg-amber-500 text-slate-900' : 'bg-slate-800 text-slate-400 hover:text-white'}`}
                    >
                      <Icon size={12} />
                      {label}
                    </button>
                  ))}
                  {isDrawing && (
                    <>
                      <span className="text-xs text-blue-400 ml-2">
                        {drawPoints.length} point{drawPoints.length !== 1 ? 's' : ''} — double-click to close polygon
                      </span>
                      <button onClick={cancelDraw} className="ml-auto flex items-center gap-1 text-xs text-red-400 hover:text-red-300 px-2 py-1 rounded bg-red-900/20">
                        <RotateCcw size={11} /> Cancel
                      </button>
                    </>
                  )}
                  <div className="ml-auto">
                    <ZoneLegend />
                  </div>
                </div>

                {/* SVG Map */}
                <div className="flex-1 relative overflow-hidden">
                  <svg
                    ref={svgRef}
                    viewBox={`0 0 ${SVG_W} ${SVG_H}`}
                    className={`w-full h-full ${drawMode === 'draw' ? 'cursor-crosshair' : 'cursor-default'}`}
                    onClick={handleSVGClick}
                    onDoubleClick={handleSVGDblClick}
                  >
                    {/* Background grid */}
                    <defs>
                      <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                        <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#1e293b" strokeWidth="0.5" />
                      </pattern>
                    </defs>
                    <rect width={SVG_W} height={SVG_H} fill="#0f172a" />
                    <rect width={SVG_W} height={SVG_H} fill="url(#grid)" />

                    {/* County boundary hint */}
                    <rect x="30" y="20" width={SVG_W - 60} height={SVG_H - 40} rx="4" fill="none" stroke="#334155" strokeWidth="1" strokeDasharray="6 4" />
                    <text x="40" y="36" fill="#475569" fontSize="10" fontFamily="monospace">Montserrado County — Cadastral Survey Area</text>

                    {/* Existing parcels */}
                    {filteredParcels.map(parcel => {
                      const pts = parcel.boundaryPoints;
                      if (pts.length < 3) return null;
                      const pathD = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ') + ' Z';
                      const zoneColor = ZONE_COLORS[parcel.zoneClass];
                      const isHovered = hoveredParcel === parcel.id;
                      const isSelected = selectedParcel?.id === parcel.id;
                      const c = centroid(pts);

                      return (
                        <g key={parcel.id}
                          onClick={e => handleParcelClick(parcel, e)}
                          onMouseEnter={() => setHoveredParcel(parcel.id)}
                          onMouseLeave={() => setHoveredParcel(null)}
                          className="cursor-pointer"
                        >
                          <path
                            d={pathD}
                            fill={zoneColor}
                            fillOpacity={isSelected ? 0.45 : isHovered ? 0.35 : 0.2}
                            stroke={isSelected ? '#f59e0b' : isHovered ? zoneColor : zoneColor}
                            strokeWidth={isSelected ? 2.5 : isHovered ? 2 : 1.5}
                            strokeOpacity={isSelected ? 1 : 0.8}
                          />
                          {/* Parcel ID label */}
                          <text x={c.x} y={c.y - 6} textAnchor="middle" fill="white" fontSize="7" fontFamily="monospace" opacity={0.9}>
                            {parcel.parcelId.split('-').slice(-1)[0]}
                          </text>
                          <text x={c.x} y={c.y + 6} textAnchor="middle" fill={zoneColor} fontSize="6" opacity={0.8}>
                            {parcel.zoneClass}
                          </text>
                          {/* Status dot */}
                          <circle cx={pts[0].x} cy={pts[0].y} r="4" fill={STATUS_CONFIG[parcel.status].color} opacity={0.9} />
                        </g>
                      );
                    })}

                    {/* In-progress drawing */}
                    {drawPoints.length > 0 && (
                      <g>
                        {drawPoints.length > 1 && (
                          <polyline
                            points={drawPoints.map(p => `${p.x},${p.y}`).join(' ')}
                            fill="none"
                            stroke="#f59e0b"
                            strokeWidth="2"
                            strokeDasharray="5 3"
                          />
                        )}
                        {drawPoints.map((pt, i) => (
                          <circle key={i} cx={pt.x} cy={pt.y} r="4" fill="#f59e0b" stroke="white" strokeWidth="1.5" />
                        ))}
                      </g>
                    )}
                  </svg>

                  {/* Map hint overlay */}
                  {drawMode === 'draw' && !isDrawing && (
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-slate-900/90 border border-amber-500/40 rounded-xl px-4 py-2 text-xs text-amber-300 flex items-center gap-2">
                      <Pencil size={12} />
                      Click on the map to place boundary vertices. Double-click to close the polygon.
                    </div>
                  )}
                </div>
              </div>

              {/* Right Panel — Detail or Parcel List */}
              <div className="w-80 bg-slate-900 border-l border-slate-700/60 flex flex-col overflow-hidden">
                {selectedParcel ? (
                  <ParcelDetailPanel
                    parcel={selectedParcel}
                    onClose={() => setSelectedParcel(null)}
                    onSave={saveParcel}
                  />
                ) : (
                  <>
                    <div className="px-4 py-3 border-b border-slate-700/60 flex items-center justify-between">
                      <h3 className="text-sm font-semibold text-white">Parcels ({filteredParcels.length})</h3>
                      <button
                        onClick={toggleSelectAll}
                        className="text-xs text-slate-400 hover:text-white flex items-center gap-1"
                      >
                        <CheckSquare size={12} />
                        {filteredParcels.every(p => p.selected) ? 'Deselect All' : 'Select All'}
                      </button>
                    </div>
                    <div className="flex-1 overflow-y-auto divide-y divide-slate-800">
                      {filteredParcels.map(parcel => {
                        const TypeIcon = PROPERTY_ICONS[parcel.propertyType];
                        const sc = STATUS_CONFIG[parcel.status];
                        return (
                          <div
                            key={parcel.id}
                            className={`px-4 py-3 hover:bg-slate-800/60 cursor-pointer transition-colors ${selectedParcel?.id === parcel.id ? 'bg-slate-800' : ''}`}
                            onClick={() => setSelectedParcel(parcel)}
                          >
                            <div className="flex items-start gap-2">
                              <button
                                onClick={e => { e.stopPropagation(); toggleSelect(parcel.id); }}
                                className="mt-0.5 flex-shrink-0 text-slate-500 hover:text-amber-400"
                              >
                                {parcel.selected ? <CheckSquare size={14} className="text-amber-400" /> : <Square size={14} />}
                              </button>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5 mb-0.5">
                                  <TypeIcon size={11} style={{ color: PROPERTY_COLORS[parcel.propertyType] }} />
                                  <span className="text-xs font-mono text-slate-300 truncate">{parcel.parcelId}</span>
                                </div>
                                <p className="text-xs text-white font-medium truncate">{parcel.ownerName}</p>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className="text-xs text-slate-500">{parcel.county}</span>
                                  <span
                                    className="text-xs px-1.5 py-0.5 rounded font-medium"
                                    style={{ color: ZONE_COLORS[parcel.zoneClass], background: `${ZONE_COLORS[parcel.zoneClass]}20` }}
                                  >
                                    {parcel.zoneClass}
                                  </span>
                                  <span className={`text-xs px-1.5 py-0.5 rounded ${sc.bg}`} style={{ color: sc.color }}>
                                    {sc.label}
                                  </span>
                                </div>
                              </div>
                              <button
                                onClick={e => { e.stopPropagation(); deleteParcel(parcel.id); }}
                                className="text-slate-600 hover:text-red-400 transition-colors flex-shrink-0"
                              >
                                <Trash2 size={12} />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            </>
          )}

          {/* List View */}
          {activeView === 'list' && (
            <div className="flex-1 overflow-auto px-6 py-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-700/60">
                    <th className="pb-3 pr-3 text-left">
                      <button onClick={toggleSelectAll} className="text-slate-400 hover:text-white">
                        <CheckSquare size={14} />
                      </button>
                    </th>
                    {['Parcel ID', 'Owner', 'County', 'Type', 'Zone', 'Area (m²)', 'Status', 'Delineated', 'Actions'].map(h => (
                      <th key={h} className="pb-3 pr-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wide whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {filteredParcels.map(parcel => {
                    const TypeIcon = PROPERTY_ICONS[parcel.propertyType];
                    const sc = STATUS_CONFIG[parcel.status];
                    return (
                      <tr key={parcel.id} className="hover:bg-slate-900/60 transition-colors">
                        <td className="py-3 pr-3">
                          <button onClick={() => toggleSelect(parcel.id)} className="text-slate-500 hover:text-amber-400">
                            {parcel.selected ? <CheckSquare size={14} className="text-amber-400" /> : <Square size={14} />}
                          </button>
                        </td>
                        <td className="py-3 pr-4">
                          <span className="font-mono text-xs text-blue-300">{parcel.parcelId}</span>
                        </td>
                        <td className="py-3 pr-4 text-white font-medium">{parcel.ownerName}</td>
                        <td className="py-3 pr-4 text-slate-400">{parcel.county}</td>
                        <td className="py-3 pr-4">
                          <div className="flex items-center gap-1.5">
                            <TypeIcon size={12} style={{ color: PROPERTY_COLORS[parcel.propertyType] }} />
                            <span className="text-slate-300">{parcel.propertyType}</span>
                          </div>
                        </td>
                        <td className="py-3 pr-4">
                          <span
                            className="text-xs px-2 py-0.5 rounded font-semibold"
                            style={{ color: ZONE_COLORS[parcel.zoneClass], background: `${ZONE_COLORS[parcel.zoneClass]}20` }}
                          >
                            {parcel.zoneClass}
                          </span>
                        </td>
                        <td className="py-3 pr-4 text-slate-300">{parcel.areaSqm.toLocaleString()}</td>
                        <td className="py-3 pr-4">
                          <span className={`text-xs px-2 py-0.5 rounded ${sc.bg}`} style={{ color: sc.color }}>
                            {sc.label}
                          </span>
                        </td>
                        <td className="py-3 pr-4 text-slate-500 text-xs">{parcel.delineatedDate}</td>
                        <td className="py-3 pr-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => { setSelectedParcel(parcel); setActiveView('map'); }}
                              className="p-1.5 rounded bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"
                              title="Edit"
                            >
                              <Edit3 size={12} />
                            </button>
                            <button
                              onClick={() => deleteParcel(parcel.id)}
                              className="p-1.5 rounded bg-slate-800 hover:bg-red-900/40 text-slate-400 hover:text-red-400 transition-colors"
                              title="Delete"
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {filteredParcels.length === 0 && (
                <div className="text-center py-16 text-slate-500">
                  <Layers size={32} className="mx-auto mb-3 opacity-40" />
                  <p>No parcels match your filters.</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── New Parcel Form Modal ─────────────────────────────────────────── */}
        {showNewParcelForm && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md shadow-2xl">
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700/60">
                <div>
                  <h2 className="text-base font-bold text-white">Assign Parcel Details</h2>
                  <p className="text-xs text-slate-400 mt-0.5">{drawPoints.length} boundary vertices captured</p>
                </div>
                <button onClick={() => { setShowNewParcelForm(false); cancelDraw(); }} className="p-1.5 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white">
                  <X size={16} />
                </button>
              </div>
              <div className="px-6 py-4 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-slate-400 mb-1">Source Property ID</label>
                    <input
                      value={newParcelForm.sourcePropertyId}
                      onChange={e => setNewParcelForm(f => ({ ...f, sourcePropertyId: e.target.value }))}
                      placeholder="S1-XXXX"
                      className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white font-mono focus:outline-none focus:border-amber-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-400 mb-1">Owner Name</label>
                    <input
                      value={newParcelForm.ownerName}
                      onChange={e => setNewParcelForm(f => ({ ...f, ownerName: e.target.value }))}
                      placeholder="Full name"
                      className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-amber-500"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-slate-400 mb-1">County</label>
                    <select
                      value={newParcelForm.county}
                      onChange={e => setNewParcelForm(f => ({ ...f, county: e.target.value }))}
                      className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-amber-500"
                    >
                      {COUNTIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-slate-400 mb-1">District</label>
                    <input
                      value={newParcelForm.district}
                      onChange={e => setNewParcelForm(f => ({ ...f, district: e.target.value }))}
                      placeholder="District"
                      className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-amber-500"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-slate-400 mb-1">Property Type</label>
                    <select
                      value={newParcelForm.propertyType}
                      onChange={e => setNewParcelForm(f => ({ ...f, propertyType: e.target.value as PropertyType }))}
                      className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-amber-500"
                    >
                      {(Object.keys(PROPERTY_ICONS) as PropertyType[]).map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-slate-400 mb-1">Zone Classification</label>
                    <select
                      value={newParcelForm.zoneClass}
                      onChange={e => setNewParcelForm(f => ({ ...f, zoneClass: e.target.value as ZoneClass }))}
                      className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-amber-500"
                    >
                      {(Object.keys(ZONE_COLORS) as ZoneClass[]).map(z => <option key={z} value={z}>{z}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Area (m²) — auto-calculated from boundary</label>
                  <input
                    type="number"
                    value={newParcelForm.areaSqm || ''}
                    onChange={e => setNewParcelForm(f => ({ ...f, areaSqm: Number(e.target.value) }))}
                    placeholder="Leave 0 to auto-calculate"
                    className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-amber-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Notes</label>
                  <textarea
                    value={newParcelForm.notes}
                    onChange={e => setNewParcelForm(f => ({ ...f, notes: e.target.value }))}
                    rows={2}
                    placeholder="Field observations, boundary notes..."
                    className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-amber-500 resize-none"
                  />
                </div>
              </div>
              <div className="px-6 py-4 border-t border-slate-700/60 flex gap-3">
                <button
                  onClick={confirmNewParcel}
                  className="flex-1 flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold py-2.5 rounded-xl transition-colors"
                >
                  <Save size={15} />
                  Save Parcel
                </button>
                <button
                  onClick={() => { setShowNewParcelForm(false); cancelDraw(); }}
                  className="px-5 py-2.5 rounded-xl border border-slate-600 text-slate-400 hover:text-white hover:border-slate-500 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Batch Forward Modal ───────────────────────────────────────────── */}
        {showBatchModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-lg shadow-2xl">
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700/60">
                <div>
                  <h2 className="text-base font-bold text-white">Batch Forward to Stage 3</h2>
                  <p className="text-xs text-slate-400 mt-0.5">Only Verified and Classified parcels will be forwarded</p>
                </div>
                <button onClick={() => setShowBatchModal(false)} className="p-1.5 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white">
                  <X size={16} />
                </button>
              </div>
              <div className="px-6 py-4">
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {parcels.filter(p => p.selected).map(parcel => {
                    const eligible = parcel.status === 'verified' || parcel.status === 'classified';
                    const sc = STATUS_CONFIG[parcel.status];
                    return (
                      <div key={parcel.id} className={`flex items-center gap-3 px-3 py-2.5 rounded-lg ${eligible ? 'bg-emerald-900/20 border border-emerald-700/30' : 'bg-slate-800/60 border border-slate-700/40 opacity-60'}`}>
                        {eligible ? <CheckCircle size={14} className="text-emerald-400 flex-shrink-0" /> : <AlertTriangle size={14} className="text-amber-400 flex-shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-mono text-slate-300">{parcel.parcelId}</p>
                          <p className="text-xs text-slate-500">{parcel.ownerName} · {parcel.zoneClass}</p>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded ${sc.bg}`} style={{ color: sc.color }}>{sc.label}</span>
                        {!eligible && <span className="text-xs text-amber-500">Skipped</span>}
                      </div>
                    );
                  })}
                </div>
                <div className="mt-4 bg-slate-800/60 rounded-xl p-3 flex items-center gap-3">
                  <Info size={14} className="text-blue-400 flex-shrink-0" />
                  <p className="text-xs text-slate-400">
                    <strong className="text-white">{parcels.filter(p => p.selected && (p.status === 'verified' || p.status === 'classified')).length}</strong> eligible parcels will be forwarded.{' '}
                    <strong className="text-amber-400">{parcels.filter(p => p.selected && p.status !== 'verified' && p.status !== 'classified').length}</strong> will be skipped (not yet verified/classified).
                  </p>
                </div>
              </div>
              <div className="px-6 py-4 border-t border-slate-700/60 flex gap-3">
                <button
                  onClick={batchForwardToStage3}
                  disabled={parcels.filter(p => p.selected && (p.status === 'verified' || p.status === 'classified')).length === 0}
                  className="flex-1 flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-40 disabled:cursor-not-allowed text-slate-900 font-bold py-2.5 rounded-xl transition-colors"
                >
                  <Send size={15} />
                  Forward to Stage 3 Verification
                </button>
                <button onClick={() => setShowBatchModal(false)} className="px-5 py-2.5 rounded-xl border border-slate-600 text-slate-400 hover:text-white hover:border-slate-500 transition-colors">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Import from Stage 1 Modal ─────────────────────────────────────── */}
        {showImportModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-lg shadow-2xl">
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700/60">
                <div>
                  <h2 className="text-base font-bold text-white">Import from Stage 1 Queue</h2>
                  <p className="text-xs text-slate-400 mt-0.5">{STAGE1_QUEUE.length} properties awaiting cadastral delineation</p>
                </div>
                <button onClick={() => setShowImportModal(false)} className="p-1.5 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white">
                  <X size={16} />
                </button>
              </div>
              <div className="px-6 py-4 space-y-2">
                {STAGE1_QUEUE.map(item => {
                  const alreadyImported = parcels.some(p => p.sourcePropertyId === item.id);
                  const TypeIcon = PROPERTY_ICONS[item.type];
                  return (
                    <div key={item.id} className="flex items-center gap-3 px-3 py-3 rounded-xl bg-slate-800/60 border border-slate-700/40">
                      <TypeIcon size={16} style={{ color: PROPERTY_COLORS[item.type] }} className="flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-mono text-blue-300">{item.id}</span>
                          <span className="text-xs text-slate-500">·</span>
                          <span className="text-xs text-slate-300">{item.type}</span>
                        </div>
                        <p className="text-sm text-white font-medium">{item.owner}</p>
                        <p className="text-xs text-slate-500">{item.county} · {item.district} · {item.area.toLocaleString()} m²</p>
                      </div>
                      {alreadyImported ? (
                        <span className="text-xs text-emerald-400 flex items-center gap-1"><CheckCircle size={12} /> Imported</span>
                      ) : (
                        <button
                          onClick={() => { importFromStage1(item); }}
                          className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold transition-colors"
                        >
                          Import
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="px-6 py-4 border-t border-slate-700/60">
                <button onClick={() => setShowImportModal(false)} className="w-full py-2.5 rounded-xl border border-slate-600 text-slate-400 hover:text-white hover:border-slate-500 transition-colors text-sm">
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
